import { useCallback, useState } from 'react';
import { SupplierThread } from '../components/forms/outreach/types';
import { outreachClient } from '../client/outreachClient';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { buildOutreachPayload } from './outreachMappers';

/**
 * Progress is keyed by the supplier on the component, not by the thread id: a
 * first send mints a real thread id in place of the draft one, and a send that
 * lost track of itself halfway through would be lying about where it got to.
 */
export const sendPhaseKey = (thread: Pick<SupplierThread, 'componentId' | 'supplierName'>): string =>
  `${thread.componentId || ''}::${thread.supplierName}`.toLowerCase();

/**
 * `rejected` never left the browser (no RFQ, no address) — there is no bubble
 * to mark. `failed` was attempted and the server said no.
 */
export interface SendOutcome {
  ok: boolean;
  rejected?: boolean;
  /** Robbie's line for a failed attempt, shown on the bubble. */
  failure?: string;
  /** The comms thread id, which a first send mints in place of the draft id. */
  threadId?: string;
}

/**
 * One buyer query, one sent email. Submitting the query IS the approval, and
 * the server composes and dispatches in the same call.
 *
 * In-flight state is tracked PER SUPPLIER THREAD: a send to one supplier must
 * neither block the composer on another nor lend its name to someone else's
 * pending bubble.
 */
export const useOutreachSend = (rfqId?: string) => {
  const [inFlight, setInFlight] = useState<Record<string, boolean>>({});
  const [sendError, setSendError] = useState<string | null>(null);

  const mark = (thread: SupplierThread, sending: boolean) =>
    setInFlight((prev) => {
      const key = sendPhaseKey(thread);
      if (sending) return { ...prev, [key]: true };
      const rest = { ...prev };
      delete rest[key];
      return rest;
    });

  const sendQuery = useCallback(
    async (thread: SupplierThread, sourceQuery: string, isNegotiation: boolean): Promise<SendOutcome> => {
      const query = sourceQuery.trim();
      if (!query) return { ok: false, rejected: true };
      if (!rfqId) {
        setSendError('I can’t find the RFQ this outreach belongs to. Reload the page and try again.');
        return { ok: false, rejected: true };
      }
      if (!thread.hasContactEmail) {
        setSendError(`I have no contact address for ${thread.supplierName} yet. Add one and I can send.`);
        return { ok: false, rejected: true };
      }
      setSendError(null);
      mark(thread, true);
      try {
        const result = await outreachClient.sendSupplierOutreach(
          rfqId,
          thread.componentId || 'comp-1',
          buildOutreachPayload(thread, query, isNegotiation),
        );
        return { ok: true, threadId: result?.data?.threadId };
      } catch (error: any) {
        console.error('Error sending outreach query:', error);
        return { ok: false, failure: apiErrorMessage(error, 'Nothing left the platform.') };
      } finally {
        mark(thread, false);
      }
    },
    [rfqId],
  );

  const isSendingThread = useCallback(
    (thread: SupplierThread | undefined | null) => Boolean(thread && inFlight[sendPhaseKey(thread)]),
    [inFlight],
  );

  return {
    /** Composes and sends in one round-trip; resolves once it has landed. */
    sendQuery,
    isSendingThread,
    /** Only for sends that never left — failed attempts live on their bubble. */
    sendError,
    dismissSendError: useCallback(() => setSendError(null), []),
  };
};
